import { faCloudCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import {
  faExclamationTriangle,
  faMicrophone,
  faSpinner,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { TooltipButton } from '@repo/videa-notes';

import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';
import type { VideaNotesAppointmentData } from '@/modules/videaNotes/types';
import {
  VideaNotesUIState,
  VoiceNotesSource,
} from '@/modules/videaNotes/types';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

interface AmbientTableActionsProps {
  user: any;
  appointment: VideaNotesAppointment;
  notes: ClinicalNote[];
  isNotesLoading?: boolean;
  isRecordingSessionActive: boolean;
  currentRecordingAppointment: VideaNotesAppointmentData | null;
  isWritebackLoading: boolean;
  openVideaNotes: (
    user: any,
    appointment: VideaNotesAppointment,
    mode: VideaNotesUIState,
    voiceNotesSource: VoiceNotesSource,
    selectedNoteId?: string,
  ) => void;
  writeBackNote: (note: ClinicalNote) => void | Promise<void>;
}

const getWritebackStatus = (note: ClinicalNote) => {
  if (!note.isWritebackEligible) return 'not-eligible';
  if (!note.writebackStatus?.clinicalNoteId) return 'ready';

  const isProcessed = note.writebackStatus?.processedAt;
  const hasError = note.writebackStatus?.errorCode !== null;

  if (hasError) return 'error';
  if (note.writebackStatus.clinicalNoteId && !isProcessed) return 'inProgress';

  return 'synced';
};

const getWritebackIcon = (writebackStatus: string, isLoading: boolean) => {
  if (isLoading || writebackStatus === 'inProgress') {
    return faSpinner;
  }
  if (writebackStatus === 'synced') {
    return faCloudCheck;
  }
  if (writebackStatus === 'error' || writebackStatus === 'no-patient') {
    return faExclamationTriangle;
  }
  return faSync;
};

const getWritebackTooltip = (writebackStatus: string, t: any) => {
  switch (writebackStatus) {
    case 'synced':
      return t('Successfully synced');
    case 'error':
      return t('Sync issue - Click to retry');
    case 'no-patient':
      return t('Cannot sync this note. No matching patient found in your PMS.');
    case 'inProgress':
      return t('Syncing...');
    default:
      return t('Ready to sync - Click to start');
  }
};

/**
 * Gets a comparable appointment identifier, handling both regular appointments
 * and standalone visits. Normalizes empty strings to undefined.
 */
const getAppointmentId = (appointment: {
  unifiedId?: string;
  standalonePatientVisitId?: string;
}): string | undefined => {
  const unifiedId =
    appointment.unifiedId === '' ? undefined : appointment.unifiedId;
  const standalonePatientVisitId =
    appointment.standalonePatientVisitId === ''
      ? undefined
      : appointment.standalonePatientVisitId;
  return unifiedId || standalonePatientVisitId;
};

export const AmbientTableActions = ({
  user,
  appointment,
  notes,
  isNotesLoading = false,
  isRecordingSessionActive,
  currentRecordingAppointment,
  isWritebackLoading,
  openVideaNotes,
  writeBackNote,
}: AmbientTableActionsProps) => {
  const { t } = useLingui();

  // Check if this is a different appointment than the one being recorded
  // Fail-safe: if recording is active but appointment data is missing, disable buttons
  // Also fail-safe when either appointment lacks a valid identifier (undefined IDs)
  const currentId = currentRecordingAppointment
    ? getAppointmentId(currentRecordingAppointment)
    : undefined;
  const appointmentId = appointment.appointment
    ? getAppointmentId(appointment.appointment)
    : undefined;
  const isDifferentAppointmentRecording =
    isRecordingSessionActive &&
    (currentRecordingAppointment === null ||
      !appointment.appointment ||
      currentId === undefined ||
      appointmentId === undefined ||
      currentId !== appointmentId);

  if (notes.length > 0) {
    const firstNote = notes[0];
    const writebackStatus = getWritebackStatus(firstNote);

    return (
      <div className="vai-flex vai-min-h-[45px] vai-items-center vai-gap-2 vai-flex-wrap">
        {/* View Notes Button */}
        <TooltipButton
          tooltip={t`View Notes`}
          dataTestId="view-notes-button"
          variant="outline"
          size="sm"
          onClick={() =>
            openVideaNotes(
              user,
              appointment,
              VideaNotesUIState.SUMMARY_RESULT,
              VoiceNotesSource.VOICE_NOTES_TABLE,
              appointment.appointment.noteId ?? undefined,
            )
          }
          disabled={isDifferentAppointmentRecording}
        >
          <Trans>View</Trans>
        </TooltipButton>

        {/* Start Note Button */}
        <TooltipButton
          icon={faMicrophone}
          tooltip={t`Start Note`}
          variant="outline"
          size="sm"
          onClick={() =>
            openVideaNotes(
              user,
              appointment,
              VideaNotesUIState.AUDIO_CAPTURE,
              VoiceNotesSource.VOICE_NOTES_TABLE,
            )
          }
          disabled={isDifferentAppointmentRecording}
        />

        {/* Writeback Status */}
        {firstNote.isWritebackEligible &&
          (writebackStatus === 'synced' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="vai-inline-flex vai-h-8 vai-w-8 vai-items-center vai-justify-center">
                  <FontAwesomeIcon
                    icon={faCloudCheck}
                    className="vai-text-green-600"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {getWritebackTooltip(writebackStatus, t)}
              </TooltipContent>
            </Tooltip>
          ) : (
            <TooltipButton
              icon={getWritebackIcon(writebackStatus, isWritebackLoading)}
              tooltip={getWritebackTooltip(writebackStatus, t)}
              variant={writebackStatus === 'error' ? 'destructive' : 'outline'}
              size="sm"
              className={
                writebackStatus === 'ready'
                  ? 'vai-bg-white hover:vai-bg-orange-50'
                  : ''
              }
              iconClassName={
                writebackStatus === 'ready' ? 'vai-text-orange-500' : ''
              }
              onClick={() => {
                if (
                  writebackStatus === 'ready' ||
                  writebackStatus === 'error'
                ) {
                  writeBackNote(firstNote);
                }
              }}
              disabled={isWritebackLoading || writebackStatus === 'inProgress'}
            />
          ))}
      </div>
    );
  } else if (isNotesLoading) {
    // Show loading state while notes for this appointment are being fetched
    return (
      <div className="vai-flex vai-h-[45px] vai-items-center vai-gap-2 vai-text-gray-500">
        <FontAwesomeIcon icon={faSpinner} spin size="sm" />
        <span className="vai-text-sm">
          <Trans>Loading...</Trans>
        </span>
      </div>
    );
  } else {
    // Show Start Note button for appointments without notes
    return (
      <div className="vai-flex vai-h-[45px] vai-items-center">
        <TooltipButton
          icon={faMicrophone}
          tooltip={t`Start Note`}
          tooltipDisabled={true}
          size="sm"
          onClick={() =>
            openVideaNotes(
              user,
              appointment,
              VideaNotesUIState.AUDIO_CAPTURE,
              VoiceNotesSource.VOICE_NOTES_TABLE,
            )
          }
          disabled={isDifferentAppointmentRecording}
        >
          <Trans>Start Note</Trans>
        </TooltipButton>
      </div>
    );
  }
};
